import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { parseCurrencyNumber } from '../../../../Utils/formatCurrency';
import { convertToVwMob } from '../../../../Utils/responsive';

import logoImg from '../../../../assets/Logo/main_logo.png';
import crownIconImg from '../../../../assets/Icon/project/crown_icon.png';
import kakaoIconImg from '../../../../assets/Icon/project/kakao_icon.png';
import banner1000ExampleImg from '../../../../assets/img/1000_example_mob.png';
import formImg from '../../../../assets/img/form.png';
import heartImg from '../../../../assets/img/heart.png';
import emotionImg from '../../../../assets/img/emotion_mob.png';
import primeBadgeImg from '../../../../assets/img/prime_badge.png';
import shareHandImg from '../../../../assets/img/share_hand.png';
import loadingLottie from '../../../../assets/Icon/lottie/Insider-loading.lottie';
import navbarExampleImg from '../../../../assets/Icon/project/navbar/navbar_mobile_example.png';
import profileDefaultImg from '../../../../assets/Icon/project/default-profile.png';

interface PreviewPartnerMobProps {
  title: string;
  projectId: string;
  goalAmount: string;
  currency: 'KRW' | 'USD';
  mediaPreviewUrl: string;
  videoPreviewSrc?: string; // Add this
  storyHtml: string;
  userFullName: string;
  membership: string;
  onClose: () => void;
  onCreate: () => void;
  creating: boolean;
  creatingLabel: string;
}

const PreviewPartnerMob: React.FC<PreviewPartnerMobProps> = ({
  title,
  projectId,
  goalAmount,
  currency,
  mediaPreviewUrl,
  storyHtml,
  userFullName,
  membership,
  onClose,
  onCreate,
  creating,
  creatingLabel,
  videoPreviewSrc,
}) => {
  const { t } = useTranslation('translation');
  const numericGoal = useMemo(() => parseCurrencyNumber(goalAmount), [goalAmount]);
  const projectTitle = title || 'Project Title';

  const today = new Date();
  const dateString = `Created ${today.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} (KR)`;

  const DonateIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width={convertToVwMob(32)} height={convertToVwMob(32)} viewBox="0 0 32 32" fill="none">
      <path d="M25.3327 18.6667C27.3193 16.72 29.3327 14.3867 29.3327 11.3333C29.3327 9.38841 28.5601 7.52315 27.1848 6.14788C25.8095 4.77262 23.9443 4 21.9993 4C19.6527 4 17.9993 4.66667 15.9993 6.66667C13.9993 4.66667 12.346 4 9.99935 4C8.05443 4 6.18917 4.77262 4.8139 6.14788C3.43863 7.52315 2.66602 9.38841 2.66602 11.3333C2.66602 14.4 4.66602 16.7333 6.66602 18.6667L15.9993 28L25.3327 18.6667Z" stroke="white" strokeWidth="2.66667" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  return (
    <PreviewDialog onPointerDown={(e) => e.stopPropagation()}>
      <PreviewTopBar>
        <PreviewTopTitle>{t('page.newProject.preview.topTitle')}</PreviewTopTitle>
        <PreviewCloseBtn type="button" onClick={onClose}>
          <svg xmlns="http://www.w3.org/2000/svg" width={convertToVwMob(20)} height={convertToVwMob(20)} viewBox="0 0 20 20" fill="none">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M19.3072 1.70719C19.4947 1.51965 19.6001 1.26528 19.6001 1.00005C19.6001 0.734821 19.4947 0.480454 19.3072 0.292908C19.1196 0.105362 18.8653 0 18.6 0C18.3348 0 18.0805 0.105362 17.8929 0.292908L9.80005 8.38577L1.70719 0.292908C1.51965 0.105362 1.26528 1.93113e-08 1.00005 2.12874e-08C0.734821 2.32635e-08 0.480454 0.105362 0.292908 0.292908C0.105362 0.480454 2.32635e-08 0.734821 2.12874e-08 1.00005C1.93113e-08 1.26528 0.105362 1.51965 0.292908 1.70719L8.38577 9.80005L0.292908 17.8929C0.105362 18.0805 0 18.3348 0 18.6C0 18.8653 0.105362 19.1196 0.292908 19.3072C0.480454 19.4947 0.734821 19.6001 1.00005 19.6001C1.26528 19.6001 1.51965 19.4947 1.70719 19.3072L9.80005 11.2143L17.8929 19.3072C18.0805 19.4947 18.3348 19.6001 18.6 19.6001C18.8653 19.6001 19.1196 19.4947 19.3072 19.3072C19.4947 19.1196 19.6001 18.8653 19.6001 18.6C19.6001 18.3348 19.4947 18.0805 19.3072 17.8929L11.2143 9.80005L19.3072 1.70719Z"
              fill="#A6A6A6"
            />
          </svg>
        </PreviewCloseBtn>
      </PreviewTopBar>

      <PreviewNavbar src={navbarExampleImg} alt="Navbar" />
      <PreviewBody>
        <PreviewHeroWrapper>
          <PreviewHero $isVideo={!!videoPreviewSrc}>
            {mediaPreviewUrl ? (
              <>
                <PreviewHeroImg src={mediaPreviewUrl} alt="" />
                {membership !== 'general' && <PreviewPrimeBadge src={primeBadgeImg} alt="PRIME" />}
                <PreviewShareHand src={shareHandImg} alt="" />
              </>
            ) : videoPreviewSrc ? (
              <PreviewIframe
                src={videoPreviewSrc}
                title="video preview"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <PreviewEmpty>{t('page.newProject.preview.emptyImage')}</PreviewEmpty>
            )}
          </PreviewHero>
        </PreviewHeroWrapper>

        <PreviewMainContent>
            {/* Project Info Card */}
            <RightCard>
              <ProjectTitle>{projectTitle}</ProjectTitle>
              <UserProfileRow>
                <UserProfileImg src={profileDefaultImg} onError={(e) => (e.currentTarget.style.backgroundColor = '#ccc')} />
                <UserInfoCol>
                    <UserName>{userFullName || 'User Name'}</UserName>
                    <UserDateRow>
                        <UserDate>{dateString}</UserDate>
                        <KeymanBadge>키맨을 찾고 있습니다</KeymanBadge>
                    </UserDateRow>
                </UserInfoCol>
              </UserProfileRow>
              <ShareButton type="button">
                <BtnIcon><img src={kakaoIconImg} alt="kakao" width={convertToVwMob(20)} height={convertToVwMob(20)} /></BtnIcon> 공유하기
              </ShareButton>
            </RightCard>

            {/* Sponsor Card */}
            <RightCard>
                <SponsorTitle>
                    <CrownIcon src={crownIconImg} alt="crown" />
                    첫번째 후원자가 되어 주세요!
                </SponsorTitle>
                <SponsorProgressWrap>
                    <SponsorBadge>0% 달성</SponsorBadge>
                    <SponsorProgressBar>
                        <SponsorProgressFill style={{ width: '0%' }} />
                    </SponsorProgressBar>
                </SponsorProgressWrap>
                <SponsorGoal>
                    목표 {currency === 'KRW' ? '￦' : '$'} {numericGoal.toLocaleString()} / mo.
                </SponsorGoal>
                <DonateButton type="button"><DonateIcon /> 후원하기</DonateButton>
            </RightCard>

            {/* Story Card */}
            <PreviewStoryCard>
              <PreviewStoryTitle>STORY</PreviewStoryTitle>
              
              <PreviewStoryBanner src={banner1000ExampleImg} alt={t('page.newProject.preview.bannerAlt')} />

              {storyHtml ? (
                <PreviewStoryHtml dangerouslySetInnerHTML={{ __html: storyHtml }} />
              ) : (
                <PreviewStoryEmpty>{t('page.newProject.preview.emptyStory')}</PreviewStoryEmpty>
              )}

              <PreviewStoryReactions src={emotionImg} alt={t('page.newProject.preview.reactionsAlt')} />
              <PreviewStoryComment src={formImg} alt={t('page.newProject.preview.commentAlt')} />
              <PreviewHeartIcon src={heartImg} alt={t('page.newProject.preview.likeAlt')} />
            </PreviewStoryCard>
        </PreviewMainContent>

        <PreviewFooterArea>
          <FooterLogo src={logoImg} alt="Mission Fund" />
          <FooterMission>
            <FooterMissionText>{t('page.newProject.preview.footerMissionKo')}</FooterMissionText>
            <FooterMissionText $blue>MissionFund helps the Body of Christ fulfill its mission.</FooterMissionText>
          </FooterMission>

          <FooterButtons>
            <FooterButton>{t('page.newProject.preview.footerButtons.receipt')}</FooterButton>
            <FooterButton>{t('page.newProject.preview.footerButtons.donations')}</FooterButton>
            <FooterButton>{t('page.newProject.preview.footerButtons.story')}</FooterButton>
            <FooterButton>FAQ</FooterButton>
          </FooterButtons>

          <FooterDesc>
            <p>{t('page.newProject.preview.footerInfo.line1')}</p>
            <p>{t('page.newProject.preview.footerInfo.line2')}</p>
            <p>{t('page.newProject.preview.footerInfo.line3')}</p>
          </FooterDesc>

          <FooterContact>
            {t('page.newProject.preview.footerContact.line1')}<br />
            {t('page.newProject.preview.footerContact.line2')}<br />
            {t('page.newProject.preview.footerContact.line3')}
          </FooterContact>
        </PreviewFooterArea>
      </PreviewBody>
      
      <PreviewFooter>
        <PreviewConfirm
          type="button"
          onClick={onCreate}
          disabled={creating}
          $loading={creating}
          aria-busy={creating}
          aria-label={creating ? creatingLabel : undefined}
        >
          {creating ? (
            <LoadingWrapper>
              <DotLottieReact
                src={loadingLottie}
                autoplay
                loop
                style={{ width: '100%', height: '100%' }}
              />
            </LoadingWrapper>
          ) : t('common.actions.confirm')}
        </PreviewConfirm>
      </PreviewFooter>
    </PreviewDialog>
  );
};

export default PreviewPartnerMob;

// Styled Components
const PreviewDialog = styled.div`
  position: fixed;
  inset: 0;
  height: 100dvh;
  z-index: 1100;
  background: #f0f1f2;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const PreviewTopBar = styled.div`
  height: ${convertToVwMob(56)};
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #eee;
  position: relative;
  flex-shrink: 0;
  padding: 0 ${convertToVwMob(16)};
  background: #fff;
`;

const PreviewTopTitle = styled.div`
  font-family: Pretendard;
  font-size: ${convertToVwMob(24)};
  font-weight: 600;
  color: #000;
  line-height: 150%;
`;

const PreviewCloseBtn = styled.button`
  margin-left: auto;
  height: ${convertToVwMob(40)};
  width: ${convertToVwMob(40)};
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const PreviewNavbar = styled.img`
  width: 100%;
  height: auto;
  display: block;
  flex-shrink: 0;
`;

const PreviewBody = styled.div`
  flex: 1;
  min-height: 0;
  background: #f0f1f2;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  touch-action: pan-y;
  padding-bottom: ${convertToVwMob(20)};
`;

const PreviewHeroWrapper = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16/9;
  filter: drop-shadow(0 4px 20px rgba(0, 0, 0, 0.1));
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PreviewHero = styled.div<{ $isVideo?: boolean }>`
  width: 100%;
  height: 100%;
  position: relative;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  clip-path: ${({ $isVideo }) => ($isVideo ? 'none' : 'polygon(0 0, 100% 0, 100% 100%, 50% 90%, 0 100%)')};
`;

const PreviewPrimeBadge = styled.img`
  position: absolute;
  top: 0;
  left: ${convertToVwMob(12)};
  width: ${convertToVwMob(54)};
  height: auto;
  z-index: 1;
`;

const PreviewShareHand = styled.img`
  position: absolute;
  bottom: 0;
  right: ${convertToVwMob(20)};
  width: ${convertToVwMob(80)};
  height: auto;
  z-index: 1;
`;

const PreviewHeroImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PreviewIframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
`;

const PreviewEmpty = styled.div`
  color: #bcbcbc;
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-weight: 600;
`;

const PreviewMainContent = styled.div`
  width: 100%;
  padding: ${convertToVwMob(16)};
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(20)};
  margin-top: ${convertToVwMob(10)};
`;

const RightCard = styled.div`
  background: #fff;
  border-radius: ${convertToVwMob(24)};
  padding: ${convertToVwMob(20)};
  box-shadow: 0 3px 10px 0 rgba(0, 0, 0, 0.50);
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const ProjectTitle = styled.div`
  color: #171717;
  font-family: "Noto Sans KR";
  font-size: ${convertToVwMob(20)};
  font-style: normal;
  font-weight: 600;
  line-height: 150%;
  margin-bottom: ${convertToVwMob(12)};
  word-break: keep-all;
  word-wrap: break-word;
`;

const UserProfileRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${convertToVwMob(10)};
  margin-bottom: ${convertToVwMob(16)};
  min-width: 0;
`;

const UserProfileImg = styled.img`
  width: ${convertToVwMob(60)};
  height: ${convertToVwMob(60)};
  border-radius: 50%;
  object-fit: cover;
  background-color: #eee;
  flex-shrink: 0;
`;

const UserInfoCol = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
  flex: 1;
`;

const UserName = styled.div`
  color: #323231;
  font-family: "Noto Sans KR";
  font-size: ${convertToVwMob(14)};
  font-style: normal;
  font-weight: 600;
  line-height: 1.2;
  max-width: 100%;
  overflow-wrap: anywhere;
  word-break: break-word;
`;

const UserDateRow = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: ${convertToVwMob(4)};
    min-width: 0;
    max-width: 100%;
`;

const KeymanBadge = styled.div`
    border-radius: 9999px;
    background: #CCC;
    color: #F7F1F1;
    text-align: center;
    font-family: "Noto Sans KR";
    font-size: ${convertToVwMob(12)};
    font-style: normal;
    font-weight: 350;
    line-height: ${convertToVwMob(18)};
    padding: ${convertToVwMob(2)} ${convertToVwMob(10)};
    margin-top: ${convertToVwMob(4)};
`;

const UserDate = styled.div`
    color: #5B5B5A;
    font-family: "Noto Sans KR";
    font-size: ${convertToVwMob(14)};
    font-style: normal;
    font-weight: 350;
    line-height: ${convertToVwMob(21)};
    max-width: 100%;
    overflow-wrap: anywhere;
    word-break: break-word;
`;

const ShareButton = styled.button`
  width: 100%;
  height: ${convertToVwMob(44)};
  border-radius: ${convertToVwMob(8)};
  background: #FFDD33;
  border: none;
  font-size: ${convertToVwMob(15)};
  font-weight: 600;
  color: #17171b;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${convertToVwMob(6)};
  cursor: pointer;
`;

const BtnIcon = styled.span`
    font-size: ${convertToVwMob(20)};
    display: flex;
`;

const SponsorTitle = styled.div`
  color: #171717;
  font-family: "Noto Sans KR";
  font-size: ${convertToVwMob(18)};
  font-style: normal;
  font-weight: 600;
  line-height: 1.2;
  margin-bottom: ${convertToVwMob(12)};
  display: flex;
  align-items: center;
  gap: ${convertToVwMob(8)};
`;

const CrownIcon = styled.img`
    width: ${convertToVwMob(24)};
    height: ${convertToVwMob(24)};
`;

const SponsorProgressWrap = styled.div`
  margin-bottom: ${convertToVwMob(12)};
`;

const SponsorBadge = styled.div`
  color: #FFF;
  font-family: "Noto Sans KR";
  font-size: ${convertToVwMob(14)};
  font-style: normal;
  font-weight: 350;
  line-height: ${convertToVwMob(20)};
  border-radius: ${convertToVwMob(8)};
  background: #B3D334;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.10), 0 1px 2px -1px rgba(0, 0, 0, 0.10);
  padding: ${convertToVwMob(4)} ${convertToVwMob(12)};
  display: inline-block;
  margin-bottom: ${convertToVwMob(6)};
`;

const SponsorProgressBar = styled.div`
  height: ${convertToVwMob(6)};
  background: #eee;
  border-radius: ${convertToVwMob(3)};
  overflow: hidden;
`;

const SponsorProgressFill = styled.div`
  height: 100%;
  background: #B3D334;
`;

const SponsorGoal = styled.div`
  color: #5B5B5A;
  font-family: "Noto Sans KR";
  font-size: ${convertToVwMob(16)};
  font-style: normal;
  font-weight: 350;
  line-height: 1.4;
  text-align: left;
  margin-bottom: ${convertToVwMob(16)};
`;

const DonateButton = styled.button`
  width: 100%;
  height: ${convertToVwMob(60)};
  border-radius: ${convertToVwMob(8)};
  background: #2BB1E6;
  border: none;
  text-align: center;
  font-family: "Noto Sans KR";
  font-size: ${convertToVwMob(24)};
  font-style: normal;
  font-weight: 350;
  line-height: 1.3;
  color: #FFF;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${convertToVwMob(8)};
  cursor: pointer;
`;

const PreviewStoryCard = styled.div`
  background: #ffffff;
  border-radius: ${convertToVwMob(24)};
  padding: ${convertToVwMob(32)} ${convertToVwMob(24)};
  box-shadow: 0 3px 10px 0 rgba(0, 0, 0, 0.50);
  display: flex;
  flex-direction: column;
`;

const PreviewStoryTitle = styled.div`
  color: #17171b;
  font-family: Pretendard;
  font-size: ${convertToVwMob(18)};
  font-weight: 400;
  line-height: 150%;
  margin-bottom: ${convertToVwMob(12)};
`;

const PreviewStoryBanner = styled.img`
  width: 100%;
  height: auto;
  border-radius: ${convertToVwMob(10)};
  margin-bottom: ${convertToVwMob(32)};
  cursor: pointer;
  display: block;
`;

const PreviewStoryHtml = styled.div`
  color: #444;
  font-family: Pretendard;
  font-size: ${convertToVwMob(16)};
  font-weight: 400;
  line-height: 1.7;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: break-word;
  margin-bottom: ${convertToVwMob(60)};

  p,
  div,
  span,
  li,
  ul,
  ol {
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  figure[data-embed='story-image'] {
    margin: ${convertToVwMob(16)} 0;
    border-radius: ${convertToVwMob(10)};
    overflow: hidden;
    background: #f0f1f2;
  }

  figure[data-embed='story-image'] > img[data-embed='image'] {
    max-width: 100%;
    height: auto;
    display: block;
  }
`;

const PreviewStoryReactions = styled.img`
  width: 100%;
  height: auto;
  margin-bottom: ${convertToVwMob(24)};
  display: block;
`;

const PreviewStoryComment = styled.img`
  width: 100%;
  height: auto;
  display: block;
  cursor: pointer;
`;

const PreviewHeartIcon = styled.img`
  width: ${convertToVwMob(44)};
  height: ${convertToVwMob(44)};
  align-self: flex-end;
  margin-top: ${convertToVwMob(20)};
  cursor: pointer;
`;

const PreviewStoryEmpty = styled.div`
  color: #bcbcbc;
  font-family: Pretendard;
  font-size: ${convertToVwMob(16)};
  font-weight: 400;
  text-align: center;
  padding: ${convertToVwMob(40)} 0;
`;

const PreviewFooterArea = styled.div`
  width: 100%;
  margin-top: ${convertToVwMob(60)};
  background: #f0f1f2;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${convertToVwMob(80)} ${convertToVwMob(24)} ${convertToVwMob(120)};
`;

const FooterLogo = styled.img`
  height: ${convertToVwMob(54)};
  margin-bottom: ${convertToVwMob(24)};
`;

const FooterMission = styled.div`
  text-align: center;
  margin-bottom: ${convertToVwMob(32)};
`;

const FooterMissionText = styled.div<{ $blue?: boolean }>`
  color: #27AAE2;
  text-align: center;
  font-family: "Noto Sans KR";
  font-size: ${convertToVwMob(20)};
  font-style: normal;
  font-weight: 350;
  line-height: 1.5;
`;

const FooterButtons = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${convertToVwMob(10)};
  margin-bottom: ${convertToVwMob(10)};
  width: 100%;
`;

const FooterButton = styled.div`
  background: #888;
  color: #fff;
  padding: ${convertToVwMob(12)} ${convertToVwMob(24)};
  border-radius: ${convertToVwMob(6)};
  font-family: Pretendard;
  font-size: ${convertToVwMob(14)};
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  white-space: nowrap;
`;

const FooterDesc = styled.div`
  text-align: left;
  color: #374151;
  font-family: "Noto Sans KR";
  font-size: ${convertToVwMob(16)};
  font-style: normal;
  font-weight: 350;
  line-height: 1.4;
  width: 100%;
  margin-top: ${convertToVwMob(32)};
  
  p {
    margin-bottom: ${convertToVwMob(16)};
    word-break: keep-all;
  }
`;

const FooterContact = styled.div`
  text-align: center;
  color: #6B7280;
  font-family: "Noto Sans KR";
  font-size: ${convertToVwMob(12)};
  font-style: normal;
  font-weight: 350;
  line-height: 1.4;
  width: 100%;
  border-top: 1px solid #aaa;
  padding-top: ${convertToVwMob(24)};
  margin-top: ${convertToVwMob(24)};
`;

const PreviewFooter = styled.div`
  position: relative;
  flex: 0 0 auto;
  z-index: 10;
  width: 100%;
  box-sizing: border-box;
  background: #ffffff;
  border-top: 1px solid #eeeeee;
  display: flex;
  justify-content: center;
  padding: ${convertToVwMob(12)} ${convertToVwMob(16)} calc(${convertToVwMob(24)} + env(safe-area-inset-bottom));
`;

const PreviewConfirm = styled.button<{ $loading?: boolean }>`
  width: 100%;
  height: ${convertToVwMob(64)};
  border-radius: ${({ $loading }) => ($loading ? convertToVwMob(10) : convertToVwMob(8))};
  border: none;
  background: ${({ $loading }) => ($loading ? '#EBFCFF' : '#0b6773')};
  color: ${({ $loading }) => ($loading ? '#2BC2E1' : '#ffffff')};
  font-family: Pretendard;
  font-size: ${convertToVwMob(18)};
  font-weight: 800;
  cursor: pointer;
  transition: background 0.2s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${convertToVwMob(8)};
  white-space: nowrap;

  &:disabled {
    background: ${({ $loading }) => ($loading ? '#EBFCFF' : '#bcbcbc')};
    color: ${({ $loading }) => ($loading ? '#2BC2E1' : '#ffffff')};
    cursor: not-allowed;
  }
`;

const LoadingWrapper = styled.div`
  width: ${convertToVwMob(132)};
  height: ${convertToVwMob(132)};
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

